import type { ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";

import { apiClient } from "../api/client";
import type { CatalogListResponse } from "../api/types";
import { ProductCard } from "../components/ProductCard";
import { Testimonials } from "../components/Testimonials";
import { EmptyState } from "../components/EmptyState";
import { CatalogSkeleton } from "../components/LoadingSkeleton";
import { getIndustryBySlug } from "../data/industries";

/**
 * Cycle 9 STORY-803: Industry vertical hub page.
 *
 * Public route /industry/:name. Hero + challenges + recommended products + testimonials.
 * Filters products via /api/catalog and matches by industry tag.
 */
export function IndustryHubPage(): ReactElement {
  const { name } = useParams<{ name: string }>();
  const navigate = useNavigate();
  const industry = name ? getIndustryBySlug(name) : undefined;

  // Fetch all catalog and filter client-side by recommendedProductSlugs
  const catalogQuery = useQuery<CatalogListResponse>({
    queryKey: ["catalog-all-industry", industry?.slug],
    queryFn: async () => {
      const { data } = await apiClient.get<CatalogListResponse>("/api/catalog");
      return data;
    },
    enabled: !!industry,
  });

  if (!industry) {
    return (
      <EmptyState
        icon="🔍"
        title="Отрасль не найдена"
        message={`Отрасль «${name}» не описана. Доступны: mining, production, retail, energy, agro-distribution.`}
        action={
          <button className="btn-primary" onClick={() => navigate("/")}>
            На главную
          </button>
        }
      />
    );
  }

  const recommendedProducts = (catalogQuery.data?.items ?? []).filter((p) =>
    industry.recommendedProductSlugs.includes(p.slug),
  );

  return (
    <article className="industry-hub">
      <header
        className="industry-hub__hero"
        style={{
          background: `linear-gradient(135deg, ${industry.accentColor}22 0%, ${industry.accentColor}08 100%)`,
        }}
      >
        <div className="industry-hub__hero-content">
          <span
            className="industry-hub__pill"
            style={{ backgroundColor: industry.accentColor }}
          >
            {industry.label}
          </span>
          <h1 className="industry-hub__title">{industry.heroTitle}</h1>
          <p className="industry-hub__subtitle">{industry.heroSubtitle}</p>
          <div className="industry-hub__ctas">
            <Link
              to="/request-demo"
              className="btn-primary"
              style={{ backgroundColor: industry.accentColor }}
            >
              Запросить демонстрацию →
            </Link>
            <Link to="/catalog" className="btn-secondary">
              Все продукты
            </Link>
          </div>
        </div>
      </header>

      <section className="industry-hub__section">
        <h2>Контекст отрасли</h2>
        <p className="industry-hub__description">{industry.description}</p>
      </section>

      <section className="industry-hub__section">
        <h2>Ключевые задачи и наши ответы</h2>
        <div className="industry-hub__challenges">
          {industry.challenges.map((c, idx) => (
            <article key={idx} className="challenge-card">
              <h3 className="challenge-card__title">{c.title}</h3>
              <p className="challenge-card__detail">{c.detail}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="industry-hub__section">
        <h2>Рекомендуемые продукты для отрасли</h2>
        {catalogQuery.isLoading && <CatalogSkeleton count={3} />}
        {recommendedProducts.length > 0 && (
          <div className="industry-hub__products">
            {recommendedProducts.map((p) => (
              <ProductCard key={p.slug} entry={p} />
            ))}
          </div>
        )}
        {!catalogQuery.isLoading && recommendedProducts.length === 0 && (
          <p className="industry-hub__empty">
            Каталог продуктов сейчас недоступен. Попробуйте через несколько минут.
          </p>
        )}
      </section>

      <Testimonials
        title={`Отзывы из отрасли: ${industry.label}`}
        limit={3}
      />

      <footer className="industry-hub__bottom">
        <h3>Готовы обсудить применение в вашей компании?</h3>
        <Link
          to="/request-demo"
          className="btn-primary"
          style={{ backgroundColor: industry.accentColor }}
        >
          Запросить персональную демонстрацию →
        </Link>
      </footer>
    </article>
  );
}
